'use client';

import { cn } from '@sharpr/ui';
import { LEAGUE_AVG_FENCES, fenceFriendliness } from '@/lib/parkFences';

interface ParkDiagramProps {
  dimensions: {
    leftLine?: number;
    leftCenter?: number;
    center?: number;
    rightCenter?: number;
    rightLine?: number;
  };
  className?: string;
}

// League averages — shared with the research SprayChart via lib/parkFences.
const AVG = LEAGUE_AVG_FENCES;

/** Color based on deviation from league avg: short = red (hitter), deep = green (pitcher) */
function dimColor(value: number, avg: number): string {
  const dev = value - avg;
  if (dev <= -5) return '#F87171'; // short = hitter-friendly = red
  if (dev >= 5) return '#34D399';  // deep = pitcher-friendly = green
  return 'rgba(255,255,255,0.7)';  // neutral
}

/** Wall segment stroke color: average of two endpoints vs their averages */
function segmentColor(v1: number, avg1: number, v2: number, avg2: number): string {
  switch (fenceFriendliness(v1, avg1, v2, avg2)) {
    case 'short': return 'rgba(248, 113, 113, 0.5)'; // red
    case 'deep': return 'rgba(52, 211, 153, 0.5)';   // green
    default: return 'rgba(34, 197, 94, 0.35)'; // neutral/default
  }
}

export function ParkDiagram({ dimensions, className }: ParkDiagramProps) {
  const { leftLine: rawLL = 330, leftCenter: rawLC = 370, center: rawCF = 400, rightCenter: rawRC = 370, rightLine: rawRL = 330 } = dimensions;
  const leftLine = Math.round(rawLL);
  const leftCenter = Math.round(rawLC);
  const center = Math.round(rawCF);
  const rightCenter = Math.round(rawRC);
  const rightLine = Math.round(rawRL);

  const toY = (d: number) => 70 - ((d - 290) / 140) * 55;

  const pts = [
    { x: 8, y: toY(leftLine) },
    { x: 22, y: toY(leftCenter) },
    { x: 50, y: toY(center) },
    { x: 78, y: toY(rightCenter) },
    { x: 92, y: toY(rightLine) },
  ];

  const homeY = 75;
  const fillPath = `M ${pts[0].x},${pts[0].y} C ${pts[1].x},${pts[1].y} ${pts[1].x},${pts[1].y} ${pts[2].x},${pts[2].y} C ${pts[3].x},${pts[3].y} ${pts[3].x},${pts[3].y} ${pts[4].x},${pts[4].y} L 92,${homeY - 5} L 50,${homeY} L 8,${homeY - 5} Z`;

  return (
    <div className={cn('relative', className)}>
      <svg viewBox="-8 18 116 60" className="w-full h-full" preserveAspectRatio="xMidYMax meet">
        {/* Grass fill */}
        <path d={fillPath} fill="rgba(34, 197, 94, 0.06)" />

        {/* Wall segments — colored by deviation */}
        {/* Full wall as base (thin, subtle) */}
        <path
          d={`M ${pts[0].x},${pts[0].y} C ${pts[1].x},${pts[1].y} ${pts[1].x},${pts[1].y} ${pts[2].x},${pts[2].y} C ${pts[3].x},${pts[3].y} ${pts[3].x},${pts[3].y} ${pts[4].x},${pts[4].y}`}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" strokeLinecap="round"
        />
        {/* LF to LC */}
        <line x1={pts[0].x} y1={pts[0].y} x2={pts[1].x} y2={pts[1].y}
          stroke={segmentColor(leftLine, AVG.leftLine, leftCenter, AVG.leftCenter)} strokeWidth="2" strokeLinecap="round" />
        {/* LC to CF */}
        <line x1={pts[1].x} y1={pts[1].y} x2={pts[2].x} y2={pts[2].y}
          stroke={segmentColor(leftCenter, AVG.leftCenter, center, AVG.center)} strokeWidth="2" strokeLinecap="round" />
        {/* CF to RC */}
        <line x1={pts[2].x} y1={pts[2].y} x2={pts[3].x} y2={pts[3].y}
          stroke={segmentColor(center, AVG.center, rightCenter, AVG.rightCenter)} strokeWidth="2" strokeLinecap="round" />
        {/* RC to RF */}
        <line x1={pts[3].x} y1={pts[3].y} x2={pts[4].x} y2={pts[4].y}
          stroke={segmentColor(rightCenter, AVG.rightCenter, rightLine, AVG.rightLine)} strokeWidth="2" strokeLinecap="round" />

        {/* Curved wall on top for shape (thinner) */}
        <path
          d={`M ${pts[0].x},${pts[0].y} C ${pts[1].x},${pts[1].y} ${pts[1].x},${pts[1].y} ${pts[2].x},${pts[2].y} C ${pts[3].x},${pts[3].y} ${pts[3].x},${pts[3].y} ${pts[4].x},${pts[4].y}`}
          fill="none" stroke="rgba(34, 197, 94, 0.25)" strokeWidth="1" strokeLinecap="round"
        />

        {/* Foul lines */}
        <line x1="50" y1={homeY} x2={pts[0].x} y2={pts[0].y} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
        <line x1="50" y1={homeY} x2={pts[4].x} y2={pts[4].y} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
        {/* Home plate */}
        <circle cx="50" cy={homeY} r="1.5" fill="rgba(255,255,255,0.3)" />

        {/* Distance labels — colored by deviation */}
        <text x="50" y={pts[2].y - 5} fontSize="9.5" fill={dimColor(center, AVG.center)} textAnchor="middle" fontWeight="700" fontFamily="monospace">
          {center}
        </text>
        <text x={pts[0].x - 2} y={pts[0].y + 1} fontSize="7.5" fill={dimColor(leftLine, AVG.leftLine)} textAnchor="end" fontWeight="600" fontFamily="monospace">
          {leftLine}
        </text>
        <text x={pts[4].x + 2} y={pts[4].y + 1} fontSize="7.5" fill={dimColor(rightLine, AVG.rightLine)} textAnchor="start" fontWeight="600" fontFamily="monospace">
          {rightLine}
        </text>
        <text x={pts[1].x - 1} y={pts[1].y - 2} fontSize="7.5" fill={dimColor(leftCenter, AVG.leftCenter)} textAnchor="end" fontWeight="600" fontFamily="monospace">
          {leftCenter}
        </text>
        <text x={pts[3].x + 1} y={pts[3].y - 2} fontSize="7.5" fill={dimColor(rightCenter, AVG.rightCenter)} textAnchor="start" fontWeight="600" fontFamily="monospace">
          {rightCenter}
        </text>
      </svg>
    </div>
  );
}
